/**
 * 使用说明：
 * - 使用 sessionStorage 缓存共享数据，默认过期时间为 5 分钟（CACHE_TTL_MS）
 * - 提供通用 getSession / setSession / removeSession，以及按需获取并缓存的 getOrFetch... 函数
 *
 * 兼容性：
 * - 若 sessionStorage 不可用（异常被捕获），getOrFetch 系列函数回退为直接调用 fetchFn（不抛错）
 *
 * 注意：该模块仅负责缓存逻辑，不在页面层触发任何 setState 或副作用。
 */

/**
 * 默认缓存过期时间：5 分钟（毫秒）
 */
const CACHE_TTL_MS = 5 * 60 * 1000;

interface CacheWrapper<T> {
  ts: number;
  data: T;
}

const isSessionAvailable = (() => {
  try {
    const key = '__ps_session_test__';
    sessionStorage.setItem(key, '1');
    sessionStorage.removeItem(key);
    return true;
  } catch (e) {
    return false;
  }
})();

/**
 * 通用 sessionStorage 接口（带过期判断）
 */
export function getSession<T>(key: string): T | null {
  if (!isSessionAvailable) return null;
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed: CacheWrapper<T> = JSON.parse(raw);
    if (!parsed || typeof parsed.ts !== 'number') return null;
    if (Date.now() - parsed.ts > CACHE_TTL_MS) {
      // 过期
      sessionStorage.removeItem(key);
      return null;
    }
    return parsed.data;
  } catch (e) {
    // 解析或访问失败则视为无缓存
    return null;
  }
}

export function setSession<T>(key: string, value: T): void {
  if (!isSessionAvailable) return;
  try {
    const wrapper: CacheWrapper<T> = { ts: Date.now(), data: value };
    sessionStorage.setItem(key, JSON.stringify(wrapper));
  } catch (e) {
    // ignore
  }
}

export function removeSession(key: string): void {
  if (!isSessionAvailable) return;
  try {
    sessionStorage.removeItem(key);
  } catch (e) {
    // ignore
  }
}

/**
 * 按需获取函数：
 * - 优先从 sessionStorage 读取（并校验过期）
 * - 若命中直接返回缓存数据
 * - 若未命中或 sessionStorage 不可用，调用 fetchFn 获取数据，并将结果写入缓存（若可用）
 *
 * 约定：这些函数返回的都是后端有效载荷（data 对象），与原 API 的 `.data` 保持一致，以便页面层逻辑最小改动。
 */

/**
 * 分享配置信息缓存：'ps_sharePileInfo'
 * fetchFn 应返回 Promise<{ code, data }>
 */
export async function getOrFetchSharePileInfo(fetchFn: () => Promise<any>, needRefresh?: boolean): Promise<any> {
  const key = 'ps_sharePileInfo';
  if (isSessionAvailable && !needRefresh) {
    const cached = getSession<any>(key);
    if (cached) return cached;
  }
  try {
    const res = await fetchFn();
    const payload = res?.data ? res?.data : res;
    if (isSessionAvailable && typeof payload !== 'undefined') {
      setSession(key, payload);
    }
    return payload;
  } catch (e) {
    // 回退：直接抛出或返回 null 保持页面原有错误处理，这里返回 null 以避免未捕获异常
    return null;
  }
}

/**
 * 站点详情缓存（按 stationId 分片）：'ps_stationDtl_{stationId}'
 * fetchFn 接受 stationId 并返回 Promise<{ code, data }>
 */
export async function getOrFetchStationDtl(keyStationId: string, fetchFn: (stationId: string) => Promise<any>): Promise<any> {
  const key = `ps_stationDtl_${keyStationId}`;
  if (isSessionAvailable) {
    const cached = getSession<any>(key);
    if (cached) return cached;
  }
  try {
    const res = await fetchFn(keyStationId);
    const payload = res?.data ? res?.data : res;
    if (isSessionAvailable && typeof payload !== 'undefined') {
      setSession(key, payload);
    }
    return payload;
  } catch (e) {
    return null;
  }
}

export async function removeStationDtl(keyStationId: string): Promise<void> {
  const key = `ps_stationDtl_${keyStationId}`;
  removeSession(key);
}
/**
 * 当前登录用户缓存：'ps_userInfo'
 * fetchFn 应返回 Promise<{ code, data }>
 */
export async function getOrFetchCurrentUser(fetchFn: () => Promise<any>): Promise<any> {
  const key = 'ps_userInfo';
  if (isSessionAvailable) {
    const cached = getSession<any>(key);
    if (cached) return cached;
  }
  try {
    const res = await fetchFn();
    const payload = res?.data ? res?.data : res;
    if (isSessionAvailable && typeof payload !== 'undefined') {
      setSession(key, payload);
    }
    return payload;
  } catch (e) {
    return null;
  }
}